import React from "react";
import { Grid, Card, CardActionArea, CardContent, Typography, Box, Checkbox, alpha } from "@mui/material";
import { Folder } from "@mui/icons-material";
import MimeIcon from "./MimeIcon";
import { humanReadableSize } from "./app/utils";

export interface FileItem {
  key: string;
  size: number;
  uploaded: string;
  httpMetadata: {
    contentType: string;
  };
  customMetadata: {
    thumbnail?: string;
  };
}

function extractFilename(key: string) {
  return key.split("/").pop();
}

export function encodeKey(key: string) {
  return key.split("/").map(encodeURIComponent).join("/");
}

export function isDirectory(file: FileItem) {
  return file.httpMetadata?.contentType === "application/x-directory";
}

interface FileGridProps {
  files: FileItem[];
  onCwdChange: (path: string) => void;
  multiSelected: string[] | null;
  onMultiSelect: (key: string) => void;
  emptyMessage: React.ReactNode;
}

export default function FileGrid({
  files,
  onCwdChange,
  multiSelected,
  onMultiSelect,
  emptyMessage,
}: FileGridProps) {
  const isSelected = (key: string) => multiSelected?.includes(key) || false;

  return files.length === 0 ? (
    emptyMessage
  ) : (
    <Grid container spacing={{ xs: 2, sm: 2.5 }} sx={{ p: { xs: 2, sm: 3 }, pb: 10 }}>
      {files.map((file) => {
        const isDir = isDirectory(file);
        const selected = isSelected(file.key);

        return (
          <Grid item key={file.key} xs={6} sm={4} md={3} lg={2.4} xl={2}>
            <Card
              elevation={0}
              sx={{
                height: '100%',
                position: 'relative',
                border: '1px solid',
                borderColor: selected ? 'primary.main' : 'transparent',
                backgroundColor: selected ? 'rgba(26, 115, 232, 0.08)' : 'background.paper',
                borderRadius: 2,
                transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
                cursor: 'pointer',
                '&:hover': {
                  borderColor: selected ? 'primary.main' : 'rgba(60,64,67,0.1)',
                  backgroundColor: selected ? 'rgba(26, 115, 232, 0.12)' : '#f8f9fa',
                  boxShadow: '0 1px 2px 0 rgba(60,64,67,0.3), 0 2px 6px 2px rgba(60,64,67,0.15)',
                  '& .file-checkbox': {
                    opacity: 1,
                  },
                },
              }}
            >
              <CardActionArea
                onClick={() => {
                  if (multiSelected !== null) {
                    onMultiSelect(file.key);
                  } else if (isDir) {
                    onCwdChange(file.key + "/");
                  } else {
                    window.open(
                      `/webdav/${encodeKey(file.key)}`,
                      "_blank",
                      "noopener,noreferrer"
                    );
                  }
                }}
                onContextMenu={(e) => {
                  e.preventDefault();
                  onMultiSelect(file.key);
                }}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'stretch',
                  userSelect: 'none',
                  borderRadius: 2,
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                }}
              >
                <Checkbox
                  checked={selected}
                  size="small"
                  className="file-checkbox"
                  sx={{
                    position: 'absolute',
                    top: 8,
                    left: 8,
                    zIndex: 1,
                    backgroundColor: 'background.paper',
                    borderRadius: '50%',
                    padding: 0.5,
                    opacity: multiSelected !== null ? 1 : 0,
                    transition: 'opacity 0.2s',
                    '& .MuiSvgIcon-root': {
                      fontSize: 18,
                    },
                    '&.Mui-checked': {
                      opacity: 1,
                    },
                  }}
                />

                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    aspectRatio: '1',
                    backgroundColor: 'transparent',
                    borderRadius: 1,
                    m: 1.5,
                    mb: 0.5,
                    overflow: 'hidden',
                  }}
                >
                  {file.customMetadata?.thumbnail ? (
                    <img
                      src={`/webdav/_$flaredrive$/thumbnails/${file.customMetadata.thumbnail}.png`}
                      alt={file.key}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        borderRadius: '4px',
                      }}
                    />
                  ) : isDir ? (
                    <Folder sx={{ fontSize: { xs: 56, sm: 64 }, color: '#5f6368' }} />
                  ) : (
                    <MimeIcon contentType={file.httpMetadata.contentType} />
                  )}
                </Box>

                <CardContent sx={{ pt: 0, pb: 1.5, px: 1.5 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      mb: 0.25,
                      fontSize: '0.875rem',
                      color: 'text.primary',
                      fontWeight: 400,
                    }}
                    title={extractFilename(file.key)}
                  >
                    {extractFilename(file.key)}
                  </Typography>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                        fontSize: '0.75rem',
                        display: { xs: 'none', sm: 'block' },
                      }}
                    >
                      {new Date(file.uploaded).toLocaleDateString(undefined, {
                        month: 'short',
                        day: 'numeric'
                      })}
                    </Typography>
                    {!isDir && (
                      <>
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'text.secondary',
                            fontSize: '0.75rem',
                            display: { xs: 'none', sm: 'inline' },
                          }}
                        >
                          •
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'text.secondary',
                            fontSize: '0.75rem',
                          }}
                        >
                          {humanReadableSize(file.size)}
                        </Typography>
                      </>
                    )}
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );
}
